import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import ROUTES from '../../../library/constants/routeConstants';
import './SideNav.scss';
import styles from '../../../assets/styles/variables.module.scss';
import { useAppDispatch } from '../../../main/hooks/hooks';
import { callResetQueryForm } from '../../../main/slices/querySlice';

interface SideNavProps {
  open: boolean;
}

export const handleNewQuery = (dispatch: AppDispatch, navigate: (path: string) => void) => {
    dispatch(callResetQueryForm());
    navigate(ROUTES.QUERY_PAGE);
    const newQueryButton = document.querySelector('[title="Reset query page"]');
    if (newQueryButton) {
        (newQueryButton as HTMLElement).click();
    }
};

const SideNav: FC<SideNavProps> = ({ open }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isActive = (path: string) => {
    if (path === ROUTES.QUERY_PAGE) {
      return location.pathname.includes('query');
    }
    else if (path === ROUTES.REPORTS) {
      return location.pathname.includes('reports');
    }
    return location.pathname === path;
  };

  return (
    <Drawer
      variant="permanent"
      className="side-nav"
      sx={{
        width: styles.sideNav_width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: styles.sideNav_width,
          boxSizing: 'border-box',
          backgroundColor: process.env.REACT_APP_SKIN_NAME === 'atai' ? 'transparent' : '#192943',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '20px 0',
          marginLeft: '10px'
          
        },
      }}
    >
      <div className="nav-buttons">
        <Tooltip title="Dashboard" placement="right">
          <IconButton
            aria-label="Dashboard"
            className={isActive(ROUTES.DASHBOARD) ? 'active' : ''}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 2C10 0.89543 10.8954 0 12 0H19C20.1046 0 21 0.895431 21 2V11C21 12.1046 20.1046 13 19 13H12C10.8954 13 10 12.1046 10 11V2Z" fill={isActive(ROUTES.DASHBOARD) ? '#FBFBFB' : styles.color_on_background}/>
              <path d="M10 17C10 15.8954 10.8954 15 12 15H19C20.1046 15 21 15.8954 21 17V20C21 21.1046 20.1046 22 19 22H12C10.8954 22 10 21.1046 10 20V17Z" fill={isActive(ROUTES.DASHBOARD) ? '#FBFBFB' : styles.color_on_background}/>
              <path d="M0 12C0 10.8954 0.895431 10 2 10H6C7.10457 10 8 10.8954 8 12V20C8 21.1046 7.10457 22 6 22H2C0.895431 22 0 21.1046 0 20V12Z" fill={isActive(ROUTES.DASHBOARD) ? '#FBFBFB' : styles.color_on_background}/>
              <path d="M0 2C0 0.895431 0.895431 0 2 0H6C7.10457 0 8 0.895431 8 2V6C8 7.10457 7.10457 8 6 8H2C0.895431 8 0 7.10457 0 6V2Z" fill={isActive(ROUTES.DASHBOARD) ? '#FBFBFB' : styles.color_on_background}/>
            </svg>
          </IconButton>
        </Tooltip>
        <Tooltip title="Upload" placement="right">
          <IconButton
            aria-label="Upload"
            className={isActive(ROUTES.UPLOAD) ? 'active' : ''}
            onClick={() => navigate(ROUTES.UPLOAD)}
          >
            {isActive(ROUTES.UPLOAD) && <svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 26.7156V2.71558C1 1.88715 1.67157 1.21558 2.5 1.21558H16.4898L23 7.99382V26.7156C23 27.544 22.3284 28.2156 21.5 28.2156H2.5C1.67157 28.2156 1 27.544 1 26.7156Z" fill="#FBFBFB"/>
                <path d="M16.4898 1.21558V6.49382C16.4898 7.32225 17.1614 7.99382 17.9898 7.99382H23M16.4898 1.21558H2.5C1.67157 1.21558 1 1.88715 1 2.71558V26.7156C1 27.544 1.67157 28.2156 2.5 28.2156H21.5C22.3284 28.2156 23 27.544 23 26.7156V7.99382M16.4898 1.21558L23 7.99382" stroke="#192943" stroke-width="1.2"/>
                <path d="M11.2244 23.3067C11.2244 23.7209 11.5602 24.0567 11.9744 24.0567C12.3886 24.0567 12.7244 23.7209 12.7244 23.3067L11.2244 23.3067ZM12.5047 11.7309C12.2118 11.438 11.7369 11.438 11.444 11.7309L6.67106 16.5039C6.37817 16.7968 6.37817 17.2716 6.67106 17.5645C6.96396 17.8574 7.43883 17.8574 7.73172 17.5645L11.9744 13.3219L16.217 17.5645C16.5099 17.8574 16.9848 17.8574 17.2777 17.5645C17.5706 17.2716 17.5706 16.7968 17.2777 16.5039L12.5047 11.7309ZM12.7244 23.3067L12.7244 12.2612L11.2244 12.2612L11.2244 23.3067L12.7244 23.3067Z" fill="#192943"/>
            </svg>}
            {!isActive(ROUTES.UPLOAD) && <svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 26.5V2.5C1 1.67157 1.67157 1 2.5 1H16.4898L23 7.77824V26.5C23 27.3284 22.3284 28 21.5 28H2.5C1.67157 28 1 27.3284 1 26.5Z" fill="#192943"/>
                <path d="M16.4898 1V6.27824C16.4898 7.10667 17.1614 7.77824 17.9898 7.77824H23M16.4898 1H2.5C1.67157 1 1 1.67157 1 2.5V26.5C1 27.3284 1.67157 28 2.5 28H21.5C22.3284 28 23 27.3284 23 26.5V7.77824M16.4898 1L23 7.77824" stroke="#FBFBFB" stroke-width="1.2"/>
                <path d="M11.2245 23.0911C11.2245 23.5053 11.5603 23.8411 11.9745 23.8411C12.3887 23.8411 12.7245 23.5053 12.7245 23.0911L11.2245 23.0911ZM12.5048 11.5153C12.2119 11.2224 11.7371 11.2224 11.4442 11.5153L6.67119 16.2883C6.37829 16.5812 6.37829 17.0561 6.67119 17.349C6.96408 17.6418 7.43895 17.6418 7.73185 17.349L11.9745 13.1063L16.2171 17.349C16.51 17.6418 16.9849 17.6418 17.2778 17.349C17.5707 17.0561 17.5707 16.5812 17.2778 16.2883L12.5048 11.5153ZM12.7245 23.0911L12.7245 12.0457L11.2245 12.0457L11.2245 23.0911L12.7245 23.0911Z" fill="#FBFBFB"/>
            </svg>}
          </IconButton>
        </Tooltip>
        <Tooltip title="Query" placement="right">
          <IconButton
            aria-label="Query"
            className={isActive(ROUTES.QUERY_PAGE) ? 'active' : ''}
            onClick={() => {
              handleNewQuery(dispatch, navigate);
            }}
          >
            {isActive(ROUTES.QUERY_PAGE) && <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.7462 11.8935C22.7462 18.0479 17.6543 23.037 11.3731 23.037C5.09191 23.037 0 18.0479 0 11.8935C0 5.73912 5.09191 0.75 11.3731 0.75C17.6543 0.75 22.7462 5.73912 22.7462 11.8935Z" fill="#FBFBFB"/>
                <path d="M17.1269 18.0157C17.8994 17.2588 19.1517 17.2588 19.9242 18.0157L23.4207 21.4416C24.1931 22.1985 24.1931 23.4255 23.4207 24.1824C22.6482 24.9392 21.3959 24.9392 20.6235 24.1824L17.1269 20.7564C16.3545 19.9996 16.3545 18.7725 17.1269 18.0157Z" fill="#FBFBFB"/>
                <path d="M11.5945 6.56396V17.9243M17.5283 12.378L5.93384 12.378" stroke="#192943" stroke-width="2" stroke-linecap="round"/>
            </svg>}
            {!isActive(ROUTES.QUERY_PAGE) && <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.7462 11.8935C22.7462 18.0479 17.6543 23.037 11.3731 23.037C5.09191 23.037 0 18.0479 0 11.8935C0 5.73912 5.09191 0.75 11.3731 0.75C17.6543 0.75 22.7462 5.73912 22.7462 11.8935Z" fill="#192943"/>
                <path d="M17.1269 18.0157C17.8994 17.2588 19.1517 17.2588 19.9242 18.0157L23.4207 21.4416C24.1931 22.1985 24.1931 23.4255 23.4207 24.1824C22.6482 24.9392 21.3959 24.9392 20.6235 24.1824L17.1269 20.7564C16.3545 19.9996 16.3545 18.7725 17.1269 18.0157Z" fill="#192943"/>
                <path d="M11.5945 6.56396V17.9243M17.5283 12.378L5.93384 12.378" stroke="#FBFBFB" stroke-width="2" stroke-linecap="round"/>
            </svg>}
          </IconButton>
        </Tooltip>
        <Tooltip title="History" placement="right">
          <IconButton
            aria-label="History"
            className={isActive(ROUTES.HISTORY) ? 'active' : ''}
            onClick={() => navigate(ROUTES.HISTORY)}
          >
            {isActive(ROUTES.HISTORY) && <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.4254 21.9298C20.1831 21.9298 24.8507 17.3564 24.8507 11.7149C24.8507 6.07336 20.1831 1.5 14.4254 1.5C8.66759 1.5 4 6.07336 4 11.7149C4 12.6804 4.5 15 4.5 15M4.5 15L7.44 11.08C7.46472 11.047 7.4412 11 7.4 11H4.02513H1.11019C1.06723 11 1.04427 11.0506 1.07256 11.0829L4.5 15ZM20.3616 20.1133L23.5 23C24.5952 24.0203 25.4375 24.0512 26 23.5C26.5625 22.9488 26.5 22 25.5 21L22.3604 18.3406C21.6279 19.1867 22.276 18.462 21.568 19.1557C21.0834 19.5756 21.2782 19.4975 20.3616 20.1133Z" stroke={isActive(ROUTES.HISTORY) ? '#FBFBFB' : styles.color_on_background} stroke-width="2" stroke-linecap="round"/>
                <path d="M25.5 23.5L26 21.5L23.0764 18.5764L20.5 20.5L24 23.5H25.5Z" fill="#FBFBFB"/>
                <path d="M2 11.5H6L4.33333 13.5L2 11.5Z" fill={isActive(ROUTES.HISTORY) ? '#FBFBFB' : styles.color_on_background}/>
                <path d="M14 5.5V12.5H21" stroke={isActive(ROUTES.HISTORY) ? '#FBFBFB' : styles.color_on_background} stroke-width="2" stroke-linecap="round"/>
            </svg>}
            {!isActive(ROUTES.HISTORY) && <svg width="28" height="25" viewBox="0 0 28 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.4254 21.9298C20.1831 21.9298 24.8507 17.3564 24.8507 11.7149C24.8507 6.07336 20.1831 1.5 14.4254 1.5C8.66759 1.5 4 6.07336 4 11.7149C4 12.6804 4.5 15 4.5 15M4.5 15L7.44 11.08C7.46472 11.047 7.4412 11 7.4 11H4.02513H1.11019C1.06723 11 1.04427 11.0506 1.07256 11.0829L4.5 15ZM20.3616 20.1133L23.5 23C24.5952 24.0203 25.4375 24.0512 26 23.5C26.5625 22.9488 26.5 22 25.5 21L22.3604 18.3406C21.6279 19.1867 22.276 18.462 21.568 19.1557C21.0834 19.5756 21.2782 19.4975 20.3616 20.1133Z" stroke="#192943" stroke-width="2" stroke-linecap="round"/>
                <path d="M25.5 23.5L26 21.5L23.0764 18.5764L20.5 20.5L24 23.5H25.5Z" fill="#192943"/>
                <path d="M2 11.5H6L4.33333 13.5L2 11.5Z" fill="#192943"/>
                <path d="M14 5.5V12.5H21" stroke="#192943" stroke-width="2" stroke-linecap="round"/>
            </svg>}
          </IconButton>
        </Tooltip>
        <Tooltip title="Reports" placement="right">
          <IconButton
            aria-label="Reports"
            className={isActive(ROUTES.REPORTS) ? 'active' : ''}
            onClick={() => navigate(ROUTES.REPORTS)}
          >
            <svg 
              width="24" 
              height="23" 
              viewBox="0 0 24 23" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M3.01135 10.8805L11.186 17.9676M11.186 17.9676L19.7909 3.79346M11.186 17.9676L20.5826 19.9047" 
                stroke={isActive(ROUTES.REPORTS) ? '#ffffff' : styles.color_on_background} 
                stroke-width="2"
              />
              <path 
                d="M13.8281 16.9927C13.8281 18.6497 12.4657 20.013 10.7603 20.013C9.05496 20.013 7.69257 18.6497 7.69257 16.9927C7.69257 15.3356 9.05496 13.9724 10.7603 13.9724C12.4657 13.9724 13.8281 15.3356 13.8281 16.9927ZM5.10167 10.4124C5.10167 11.5144 4.19458 12.4277 3.05083 12.4277C1.90709 12.4277 1 11.5144 1 10.4124C1 9.31049 1.90709 8.3972 3.05083 8.3972C4.19458 8.3972 5.10167 9.31049 5.10167 10.4124ZM21.9831 3.26523C21.9831 4.36717 21.076 5.28046 19.9322 5.28046C18.7885 5.28046 17.8814 4.36717 17.8814 3.26523C17.8814 2.16328 18.7885 1.25 19.9322 1.25C21.076 1.25 21.9831 2.16328 21.9831 3.26523ZM23 19.2348C23 20.3367 22.0929 21.25 20.9492 21.25C19.8054 21.25 18.8983 20.3367 18.8983 19.2348C18.8983 18.1328 19.8054 17.2195 20.9492 17.2195C22.0929 17.2195 23 18.1328 23 19.2348Z" 
                fill={isActive(ROUTES.REPORTS) ? '#ffffff' : styles.color_on_background} 
                stroke={isActive(ROUTES.REPORTS) ? '#ffffff' : styles.color_on_background} 
                stroke-width="2"
              />
            </svg>
          </IconButton>
        </Tooltip>
        <Tooltip title="System & Search Status" placement="right">
          <IconButton
            aria-label="System & Search Status"
            className={isActive(ROUTES.STATUS) ? 'active' : ''}
            onClick={() => navigate(ROUTES.STATUS)}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="sp-hb-nav-icon"
            >
              <path
                d="M2 12.4H6.2L8.1 7.2L11 16.8L13.4 5.5L15.6 12.4H18"
                stroke={isActive(ROUTES.STATUS) ? '#FBFBFB' : styles.color_on_background}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="19.6"
                cy="12.4"
                r="1.7"
                fill={isActive(ROUTES.STATUS) ? '#FBFBFB' : styles.color_on_background}
              />
            </svg>
          </IconButton>
        </Tooltip>
      </div>
    </Drawer>
  );
};

export default SideNav;